// A packet for one person (2026-10-29): pick reports and documents (by hand, by date range or all), optionally put a summary
// at the front (drafted by AI from the values on file, then read and edited here), and the worker makes one PDF.
import { useEffect, useMemo, useState } from 'react';
import { post } from '../lib/api.ts';
import { useData } from '../lib/useData.ts';
import { ErrorBox } from './Layout.tsx';
import { Modal } from './Modal.tsx';

interface Report { assessmentId: string; assessedOn: string; visitType: string; departmentName: string | null; hasPdf: boolean }
interface Doc { documentId: string; docType: string; title: string | null; date: string; assessmentId: string | null }
interface Packet { id: string; status: 'queued' | 'running' | 'done' | 'failed'; title: string | null; error: string | null; pageCount: number | null;
  items: number; hasSummary: boolean; summaryAi: boolean; createdAt: string; requestedBy: string | null }
type Item = { kind: 'report'; assessmentId: string } | { kind: 'document'; documentId: string };

const VISIT: Record<string, string> = { physical: 'Physical', cohsg: 'COHSG screening', labs: 'Labs' };
const TYPE: Record<string, string> = {
  lab_panel: 'Lab results', drug_screen: 'Drug screen', ekg: 'EKG', pft: 'Spirometry / PFT', clearance: 'Clearance form', test_result: 'Test result',
  authorization: 'Authorization / consent', medical_history: 'Medical history', prior_report: 'Prior report', outside_record: 'Outside record',
  intake_form: 'Intake form (scan)', follow_up: 'Follow-up', clearance_letter: 'Clearance letter', correspondence: 'Letter', roster: 'Roster', other: 'Document',
};
const keyOf = (i: Item) => i.kind === 'report' ? `r:${i.assessmentId}` : `d:${i.documentId}`;

export function PersonPacketsCard({ participantId }: { participantId: string }) {
  const { data, error, reload } = useData<Packet[]>(`/api/participants/${participantId}/packets`);
  const [open, setOpen] = useState(false);
  const working = (data ?? []).some((p) => p.status === 'queued' || p.status === 'running');
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void reload(), 3000);
    return () => clearInterval(t);
  }, [working, reload]);
  return (
    <div className="card no-print">
      <div className="spread">
        <h2 style={{ margin: 0 }}>Packets</h2>
        <button onClick={() => setOpen(true)}>Create packet</button>
      </div>
      <p className="muted small" style={{ marginBottom: data?.length ? undefined : 0 }}>One PDF of this person’s reports and documents (all, a date range, or the ones you pick), with a summary at the front if you like.</p>
      <ErrorBox error={error} />
      {!!data?.length && (
        <table className="small">
          <thead><tr><th>Made</th><th>Title</th><th>Contents</th><th>By</th><th></th></tr></thead>
          <tbody>{data.map((p) => (
            <tr key={p.id}>
              <td>{new Date(p.createdAt).toLocaleString()}</td>
              <td>{p.title ?? '—'}</td>
              <td>{p.items} item{p.items === 1 ? '' : 's'}{p.hasSummary ? (p.summaryAi ? ' + AI-drafted summary' : ' + summary') : ''}{p.pageCount ? ` · ${p.pageCount} pages` : ''}</td>
              <td>{p.requestedBy ?? '—'}</td>
              <td>{p.status === 'done'
                ? <span className="actions"><a className="btn" href={`/api/participants/${participantId}/packets/${p.id}/pdf`} target="_blank" rel="noopener">Open</a>
                    <a className="btn secondary" href={`/api/participants/${participantId}/packets/${p.id}/pdf?download=1`}>Download</a></span>
                : p.status === 'failed' ? <span className="small" style={{ color: '#a00' }} title={p.error ?? ''}>Failed: make it again</span>
                : <span className="badge draft">{p.status === 'running' ? 'Making PDF…' : 'Queued…'}</span>}</td>
            </tr>))}</tbody>
        </table>)}
      {open && <PacketBuilder participantId={participantId} onClose={() => setOpen(false)} onMade={() => { setOpen(false); void reload(); }} />}
    </div>
  );
}

function PacketBuilder({ participantId, onClose, onMade }: { participantId: string; onClose: () => void; onMade: () => void }) {
  const { data, error } = useData<{ reports: Report[]; documents: Doc[] }>(`/api/participants/${participantId}/packet-choices`);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [title, setTitle] = useState('');
  const [withSummary, setWithSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [aiDrafted, setAiDrafted] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);

  // Grouped by visit (newest first): each signed visit's report, then its documents; documents of other visits or none after.
  const groups = useMemo(() => {
    if (!data) return [];
    const out: { label: string; rows: { item: Item; label: string; date: string; note?: string }[] }[] = [];
    const used = new Set<string>();
    for (const r of data.reports) {
      const rows = [{ item: { kind: 'report', assessmentId: r.assessmentId } as Item, label: `Report: ${VISIT[r.visitType] ?? 'Visit'}`, date: r.assessedOn,
        note: r.hasPdf ? undefined : 'no PDF yet: made with the packet' }];
      for (const d of data.documents.filter((x) => x.assessmentId === r.assessmentId)) {
        used.add(d.documentId);
        rows.push({ item: { kind: 'document', documentId: d.documentId }, label: `${TYPE[d.docType] ?? 'Document'}${d.title ? `: ${d.title}` : ''}`, date: d.date, note: undefined });
      }
      out.push({ label: `${r.assessedOn} · ${VISIT[r.visitType] ?? 'Visit'}${r.departmentName ? ` · ${r.departmentName}` : ''}`, rows });
    }
    const rest = data.documents.filter((d) => !used.has(d.documentId));
    if (rest.length) out.push({ label: 'Other documents (not on a signed visit)', rows: rest.map((d) => ({ item: { kind: 'document', documentId: d.documentId } as Item,
      label: `${TYPE[d.docType] ?? 'Document'}${d.title ? `: ${d.title}` : ''}`, date: d.date })) });
    return out;
  }, [data]);
  const all = groups.flatMap((g) => g.rows);
  const items = all.filter((r) => picked.has(keyOf(r.item))).map((r) => r.item);
  const toggle = (k: string, on: boolean) => setPicked((s) => { const n = new Set(s); if (on) n.add(k); else n.delete(k); return n; });
  const pickRange = () => setPicked(new Set(all.filter((r) => (!from || r.date >= from) && (!to || r.date <= to)).map((r) => keyOf(r.item))));

  const draft = async () => {
    setErr(null); setBusy('draft');
    try {
      const r = await post<{ text: string }>(`/api/participants/${participantId}/packet-summary`, { items });
      setSummary(r.text); setAiDrafted(true); setChecked(false);
    } catch (x) { setErr(x); } finally { setBusy(null); }
  };
  const make = async () => {
    setErr(null);
    if (!items.length) return setErr(new Error('Tick at least one item.'));
    if (withSummary && aiDrafted && summary.trim() && !checked) return setErr(new Error('Read the AI-drafted summary and tick that you have checked it.'));
    setBusy('make');
    try {
      await post(`/api/participants/${participantId}/packets`, { items, title: title.trim() || undefined,
        summary: withSummary && summary.trim() ? summary.trim() : undefined, summaryAi: withSummary && aiDrafted });
      onMade();
    } catch (x) { setErr(x); } finally { setBusy(null); }
  };

  return (
    <Modal title="Create packet" onClose={onClose} wide>
      <div className="modal-form">
        <ErrorBox error={error} />
        {!data ? <p className="muted">Loading…</p> : !all.length ? <p>This person has no signed visits or documents to put in a packet yet.</p> : (<>
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field"><label htmlFor="pk-from">From</label><input id="pk-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="field"><label htmlFor="pk-to">To</label><input id="pk-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            <div className="field" style={{ flex: '0 0 auto' }}><button type="button" className="secondary" disabled={!from && !to} onClick={pickRange}>Tick these dates</button></div>
          </div>
          <div className="actions" style={{ marginBottom: 8 }}>
            <button type="button" className="link" onClick={() => setPicked(new Set(all.map((r) => keyOf(r.item))))}>Tick all</button>
            <button type="button" className="link" onClick={() => setPicked(new Set())}>Clear</button>
            <span className="muted small">{items.length} of {all.length} ticked · they go in the packet in this order</span>
          </div>
          <div style={{ maxHeight: '40vh', overflow: 'auto', border: '1px solid var(--line, #ddd)', borderRadius: 6, padding: '0.25rem 0.5rem' }}>
            {groups.map((g) => (
              <div key={g.label} style={{ marginBottom: 6 }}>
                <div className="small" style={{ fontWeight: 600, background: 'var(--ngf-beige-soft)', padding: '2px 6px', marginTop: 4 }}>{g.label}</div>
                {g.rows.map((r) => { const k = keyOf(r.item); return (
                  <label key={k} className="small" style={{ display: 'block', fontWeight: 400, padding: '2px 6px 2px calc(1.55rem + 6px)' }}>
                    <input type="checkbox" checked={picked.has(k)} onChange={(e) => toggle(k, e.target.checked)} />{' '}
                    {r.label} <span className="muted">· {r.date}{r.note ? ` · ${r.note}` : ''}</span>
                  </label>); })}
              </div>))}
          </div>
          <div className="field" style={{ marginTop: 10 }}><label htmlFor="pk-title">Title on the cover (optional)</label>
            <input id="pk-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. For Dr. Smith, or Respirator clearance 2026" /></div>
          <label style={{ fontWeight: 400 }}><input type="checkbox" checked={withSummary} onChange={(e) => setWithSummary(e.target.checked)} /> Add a summary at the front</label>
          {withSummary && (
            <div style={{ marginTop: 6 }}>
              <div className="actions" style={{ marginBottom: 6 }}>
                <button type="button" className="secondary" disabled={!items.length || busy !== null} onClick={() => void draft()}>{busy === 'draft' ? 'Drafting…' : summary ? 'Draft again with AI' : 'Draft with AI'}</button>
                <span className="muted small">From the values on file for the ticked items (results, labs, clearance); the AI is not sent the name, date of birth or the files.</span>
              </div>
              <textarea rows={9} style={{ width: '100%' }} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={6000}
                placeholder="Write a summary, or draft one with AI and edit it." />
              {aiDrafted && summary.trim() && (
                <label className="small" style={{ fontWeight: 400, display: 'block' }}><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />{' '}
                  I have read this AI-drafted summary and checked it against the record. <span className="muted">(The cover says it was drafted with AI and reviewed by you.)</span></label>)}
            </div>)}
        </>)}
        <ErrorBox error={err} />
        <div className="actions" style={{ marginTop: 12 }}>
          <button disabled={busy !== null || !items.length} onClick={() => void make()}>{busy === 'make' ? 'Creating…' : `Create packet (${items.length})`}</button>
          <button type="button" className="secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}
